import { css, useTheme } from "@emotion/react";
import { Children, forwardRef, Fragment } from "react";

import isMergeableObject from "./isMergeableObject";
import parseStyle from "./parseStyle.js";
import useThemeProps from "./useThemeProps.jsx";

function Divider({ as: Component = "hr", style, ...props }) {
  const theme = useTheme();
  return (
    <Component
      {...props}
      css={css`
        flex-shrink: 0;
        border-width: thin;
        border-style: none solid solid none;
        border-color: ${theme.getColor(["gray", 300])};
        height: auto;
        align-self: stretch;
        margin: 0;
        ${parseStyle(style, theme)}
      `}
    />
  );
}

export default forwardRef(function Stack(props, ref) {
  let {
    as: Component = "div",
    children,
    direction = "column",
    spacing = 2,
    justifyContent = "center",
    divider = false,
    alignItems = "stretch",
    height = "auto",
    dividerStart = false,
    dividerEnd = false,
    marginBefore = false,
    marginAfter = false,
    ...restProps
  } = useThemeProps({
    props,
    name: "Stack",
  });
  const theme = useTheme();
  const gap = theme.getLength(spacing);

  if (divider === true) {
    divider = {};
  }
  if (isMergeableObject(divider)) {
    divider = (
      <Divider
        orientation={direction === "column" ? "horizontal" : "vertical"}
        {...divider}
      />
    );
  }

  children = Children.toArray(children).filter(Boolean);

  return (
    <Component
      ref={ref}
      css={css`
        display: flex;
        flex-direction: ${direction};
        gap: ${gap};
        justify-content: ${justifyContent};
        align-items: ${alignItems};
        height: ${theme.getLength(height)};
        margin-block-start: ${marginBefore ? theme.getLength(spacing) : null};
        margin-block-end: ${marginAfter ? theme.getLength(spacing) : null};
      `}
      {...restProps}
    >
      {dividerStart && divider}
      {children.map((child, index) => (
        <Fragment key={index}>
          {child}
          {dividerEnd || (index < children.length - 1 && divider)}
        </Fragment>
      ))}
    </Component>
  );
});
